import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Star, Music, Mic2, Drama, Sparkles, Calendar, MapPin, ArrowRight } from 'lucide-react';

const POSTER = '/images/hsm-poster.jpg';
const VIDEO = '/videos/hsm-landscape.mp4';

const pillars = [
  { icon: Drama, title: 'Acting', text: 'Build confidence, characters and stage presence with professional drama training.' },
  { icon: Mic2, title: 'Singing', text: 'Learn the songs from the show and develop your voice with expert vocal coaching.' },
  { icon: Music, title: 'Dance', text: 'From "We\'re All In This Together" to full production numbers — learn real choreography.' },
];

const steps = [
  { n: '1', title: 'Book a free trial', text: 'Come and try a class — completely free, no commitment.' },
  { n: '2', title: 'Train each week', text: 'Join UPA and train in acting, singing and dance every week.' },
  { n: '3', title: 'Take the stage', text: 'Be part of High School Musical Jr — our full summer production.' },
];

export default function HighSchoolMusical() {
  return (
    <div className="min-h-screen bg-[#0a0405] text-white">
      <SEO
        title="Be Part of High School Musical Jr | Theatre School Milton Keynes | UPA"
        description="Join UPA in Milton Keynes, train in acting, singing and dance, and take part in our summer production of High School Musical Jr. New students welcome — book a free trial."
        image={`https://www.upaschool.com${POSTER}`}
        url="https://www.upaschool.com/high-school-musical"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2a0a0c] via-[#160607] to-[#0a0405]" />
        <div className="relative max-w-6xl mx-auto px-4 pt-10 pb-16 sm:pt-14 sm:pb-20">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-[#d4a017]/15 border border-[#d4a017]/40 text-[#f5c542] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
              <Star className="w-3.5 h-3.5" /> Next Summer&apos;s Show
            </span>
          </div>

          <img
            src={POSTER}
            alt="High School Musical Jr — be part of next summer's show at UPA"
            className="w-full max-w-4xl mx-auto rounded-2xl shadow-2xl shadow-black/60 border border-[#d4a017]/20"
            data-testid="hsm-poster"
          />

          <div className="text-center mt-10 max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
              Be part of <span className="text-[#f5c542]">High School Musical Jr</span> next summer
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/75">
              Join UPA, train in acting, singing and dance, and take part in our summer production.
              <br className="hidden sm:block" />
              <span className="text-white font-semibold"> New students welcome — book a free trial.</span>
            </p>
            <div className="mt-8">
              <Link to="/free-trial" data-testid="hsm-book-trial-hero">
                <Button className="bg-[#f5c542] hover:bg-[#e0b02f] text-[#2a0a0c] font-black text-lg sm:text-xl px-10 sm:px-14 py-7 rounded-full shadow-[0_0_40px_rgba(245,197,66,0.35)] hover:shadow-[0_0_60px_rgba(245,197,66,0.5)] transition-all uppercase tracking-wide">
                  Book Your Free Trial <ArrowRight className="w-6 h-6 ml-2" />
                </Button>
              </Link>
              <p className="mt-3 text-xs text-white/50">Free taster class · Ages 4–18 · Milton Keynes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <video
          src={VIDEO}
          poster={POSTER}
          autoPlay
          muted
          loop
          playsInline
          controls
          className="w-full rounded-2xl border border-[#d4a017]/20 shadow-2xl shadow-black/60"
          data-testid="hsm-video"
        />
      </section>

      {/* Train in three disciplines */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <h2 className="text-base md:text-lg font-bold tracking-[0.2em] uppercase text-[#f5c542] text-center mb-3">Join us for</h2>
        <p className="text-3xl sm:text-4xl font-black text-center mb-12">Acting, Singing &amp; Dancing</p>
        <div className="grid sm:grid-cols-3 gap-6">
          {pillars.map((p) => (
            <div key={p.title} className="bg-[#160607] border border-[#d4a017]/20 rounded-2xl p-7 text-center hover:border-[#d4a017]/50 transition-colors">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#d4a017]/15 border border-[#d4a017]/40 mb-4">
                <p.icon className="w-7 h-7 text-[#f5c542]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{p.title}</h3>
              <p className="text-sm text-white/65">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#160607] border-y border-[#d4a017]/15">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <h2 className="text-base md:text-lg font-bold tracking-[0.2em] uppercase text-[#f5c542] text-center mb-3">How it works</h2>
          <p className="text-3xl sm:text-4xl font-black text-center mb-12">From first class to opening night</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="relative bg-[#0a0405] border border-[#d4a017]/20 rounded-2xl p-7">
                <span className="absolute -top-4 left-6 w-9 h-9 flex items-center justify-center rounded-full bg-[#f5c542] text-[#2a0a0c] font-black text-lg">{s.n}</span>
                <h3 className="text-lg font-bold mt-3 mb-2">{s.title}</h3>
                <p className="text-sm text-white/65">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Details + final CTA */}
      <section className="max-w-4xl mx-auto px-4 py-14 sm:py-20 text-center">
        <Sparkles className="w-8 h-8 text-[#f5c542] mx-auto mb-4" />
        <h2 className="text-3xl sm:text-4xl font-black mb-4">New students welcome</h2>
        <p className="text-white/70 max-w-2xl mx-auto mb-8">
          No experience needed — just bags of enthusiasm. Every UPA student takes part in the show,
          so whether your child dreams of playing Troy or Gabriella or just wants to be part of something
          unforgettable, it starts with a free trial class.
        </p>
        <div className="flex flex-wrap justify-center gap-4 text-sm text-white/60 mb-10">
          <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-[#f5c542]" /> Weekly classes, ages 4–18</span>
          <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#f5c542]" /> Kents Hill, Milton Keynes</span>
        </div>
        <Link to="/free-trial" data-testid="hsm-book-trial-footer">
          <Button className="bg-[#f5c542] hover:bg-[#e0b02f] text-[#2a0a0c] font-black text-lg sm:text-xl px-10 sm:px-14 py-7 rounded-full shadow-[0_0_40px_rgba(245,197,66,0.35)] hover:shadow-[0_0_60px_rgba(245,197,66,0.5)] transition-all uppercase tracking-wide">
          Book Your Free Trial <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
